/**
 * SetFilter Component - Hierarchical Set Filter with Accordion
 * 
 * Displays card sets (like Conclave Arcana, Rebellion, etc.) with 
 * expandable categories (Core, Promo, Extra, Reward)
 */

import { useState } from "react";
import { 
  Box, 
  Typography, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails,
  useTheme,
  Tooltip
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { tokens } from "../../theme";
import { CARD_SETS } from "../../constants/filterConstants";

const SetFilter = ({ selectedCategories = [], onCategoryToggle, isMobile = false }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  // Track which accordions are expanded
  const [expandedSets, setExpandedSets] = useState(['conclave_arcana']); // Conclave Arcana expanded by default
  
  const handleAccordionChange = (setId) => (event, isExpanded) => {
    setExpandedSets(prev => 
      isExpanded 
        ? [...prev, setId]
        : prev.filter(id => id !== setId)
    );
  };
  
  const handleCategoryToggle = (setId, categoryId) => {
    const categoryKey = `${setId}_${categoryId}`;
    onCategoryToggle(categoryKey);
  };
  
  // Toggle all categories in a set
  const handleSetToggle = (event, set) => {
    event.stopPropagation(); // Prevent accordion from expanding/collapsing
    
    const allCategoryKeys = set.categories.map(cat => `${set.id}_${cat.id}`);
    const selectedInSet = allCategoryKeys.filter(key => selectedCategories.includes(key));
    
    // If all are selected, deselect all. Otherwise, select all
    if (selectedInSet.length === set.categories.length) {
      // Deselect all
      allCategoryKeys.forEach(key => onCategoryToggle(key));
    } else {
      // Select all that are not already selected
      allCategoryKeys
        .filter(key => !selectedCategories.includes(key))
        .forEach(key => onCategoryToggle(key));
    }
  };
  
  // Count selected categories in a set
  const countSelectedInSet = (set) => {
    return set.categories.filter(cat => 
      selectedCategories.includes(`${set.id}_${cat.id}`)
    ).length;
  };
  
  const fontSize = isMobile ? '0.8rem' : '0.9rem';
  const smallFontSize = isMobile ? '0.75rem' : '0.85rem';
  const iconSize = isMobile ? 20 : 24;
  const containerPadding = isMobile ? 0.5 : 1;
  
  return (
    <Box mb={isMobile ? 1 : 2}>
      <Typography
        variant={isMobile ? "caption" : "body2"}
        color={colors.grey[400]}
        fontWeight="600"
        sx={{ mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}
      >
        Card Set
      </Typography>
      
      {CARD_SETS.map(set => {
        const selectedCount = countSelectedInSet(set);
        const isExpanded = expandedSets.includes(set.id);
        
        return (
          <Accordion
            key={set.id}
            expanded={isExpanded}
            onChange={handleAccordionChange(set.id)}
            sx={{
              backgroundColor: colors.primary[400],
              boxShadow: 'none',
              border: `1px solid ${colors.primary[300]}`,
              borderRadius: '4px !important',
              mb: 0.5,
              '&:before': { display: 'none' },
              '&.Mui-expanded': {
                margin: '0 0 4px 0'
              }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: colors.grey[300], fontSize: iconSize }} />}
              sx={{
                minHeight: isMobile ? 36 : 40,
                '&.Mui-expanded': {
                  minHeight: isMobile ? 36 : 40
                },
                '& .MuiAccordionSummary-content': {
                  margin: isMobile ? '6px 0' : '8px 0',
                  '&.Mui-expanded': {
                    margin: isMobile ? '6px 0' : '8px 0'
                  }
                }
              }}
            >
              <Box display="flex" alignItems="center" gap={1} flex={1}>
                {set.icon && (
                  <Tooltip title="Click to toggle all categories in this set" arrow placement="left">
                    <Box
                      onClick={(e) => handleSetToggle(e, set)}
                      sx={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '4px',
                        borderRadius: '6px',
                        border: `2px solid ${selectedCount > 0 ? colors.blueAccent[500] : colors.grey[600]}`,
                        backgroundColor: selectedCount > 0 ? `${colors.blueAccent[800]}40` : `${colors.primary[300]}80`,
                        transition: 'all 0.3s',
                        boxShadow: selectedCount > 0 ? `0 0 8px ${colors.blueAccent[600]}40` : `0 0 4px ${colors.grey[700]}40`,
                        '&:hover': {
                          backgroundColor: selectedCount > 0 ? `${colors.blueAccent[700]}60` : colors.blueAccent[900],
                          border: `2px solid ${selectedCount > 0 ? colors.blueAccent[400] : colors.blueAccent[500]}`,
                          transform: 'scale(1.1)',
                          boxShadow: `0 0 12px ${colors.blueAccent[500]}60`
                        }
                      }}
                    >
                      <img 
                        src={set.icon} 
                        alt={set.name}
                        width={iconSize}
                        height={iconSize}
                        style={{ 
                          filter: selectedCount > 0 ? 'brightness(1.2) drop-shadow(0 0 2px rgba(66, 133, 244, 0.5))' : 'brightness(1.0)',
                          transition: 'filter 0.3s'
                        }}
                      />
                    </Box>
                  </Tooltip>
                )}
                <Typography 
                  fontSize={fontSize}
                  color={colors.grey[100]}
                  fontWeight={selectedCount > 0 ? '600' : '400'}
                  onClick={(e) => handleSetToggle(e, set)}
                  sx={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'all 0.2s',
                    '&:hover': {
                      color: colors.blueAccent[400],
                      transform: 'translateX(2px)'
                    }
                  }}
                >
                  {set.name}
                </Typography>
                {selectedCount > 0 && (
                  <Box
                    sx={{
                      ml: 'auto',
                      backgroundColor: colors.blueAccent[600],
                      borderRadius: '12px',
                      px: 0.75,
                      py: 0.25,
                      minWidth: '20px',
                      textAlign: 'center'
                    }}
                  >
                    <Typography 
                      fontSize={smallFontSize}
                      color={colors.grey[100]}
                      fontWeight="600"
                    >
                      {selectedCount}/{set.categories.length}
                    </Typography>
                  </Box>
                )}
              </Box>
            </AccordionSummary>
            
            <AccordionDetails
              sx={{
                padding: isMobile ? '4px 8px 8px' : '4px 12px 12px',
                backgroundColor: colors.primary[500]
              }}
            >
              <Box 
                display="flex" 
                justifyContent="space-around"
                sx={{ 
                  backgroundColor: colors.primary[600],
                  borderRadius: 1,
                  p: containerPadding,
                  gap: 0.5
                }}
              >
                {set.categories.map(category => {
                  const categoryKey = `${set.id}_${category.id}`;
                  const isSelected = selectedCategories.includes(categoryKey);
                  
                  return (
                    <Tooltip key={category.id} title={category.name} arrow placement="top">
                      <Box
                        onClick={() => handleCategoryToggle(set.id, category.id)}
                        sx={{
                          flex: 1,
                          height: isMobile ? 32 : 36,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: isSelected ? colors.blueAccent[600] : colors.primary[400],
                          borderRadius: '4px',
                          border: `2px solid ${isSelected ? colors.blueAccent[400] : colors.grey[600]}`,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          opacity: isSelected ? 1 : 0.7,
                          padding: '4px',
                          '&:hover': {
                            opacity: 1,
                            borderColor: isSelected ? colors.blueAccent[300] : colors.grey[400],
                            backgroundColor: isSelected ? colors.blueAccent[500] : colors.primary[300]
                          }
                        }}
                      >
                        {category.icon && (
                          <img 
                            src={category.icon} 
                            alt={category.name}
                            width={isMobile ? 18 : 22}
                            height={isMobile ? 18 : 22}
                            style={{ 
                              filter: isSelected ? 'brightness(1.1)' : 'brightness(0.8)',
                              transition: 'filter 0.2s'
                            }}
                          />
                        )}
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default SetFilter;
